import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { X, ArrowRight, MapPin } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../../lib/i18n";
import { api } from "../../lib/api";
import { DISTRIBUTOR_COUNTRIES, COUNTRY_BLURBS, GEO_NAME } from "../../lib/data";

const GEO_URL = "/geo/countries-110m.json";

// distributor country -> topojson name (reverse lookups are computed per-instance below).
const geoName = (c) => GEO_NAME[c] || c;

// Deterministic pin placement: scatter appointed distributors around the country centroid
// (stable per city+company so pins don't jump between renders).
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const offsetFor = (key) => {
  const h = hashStr(key);
  const ang = ((h % 360) * Math.PI) / 180;
  const rad = 0.6 + (((h >> 3) % 100) / 100) * 1.2; // 0.6–1.8°
  return [Math.cos(ang) * rad, Math.sin(ang) * rad];
};

// Approximate marker coordinates [lon, lat] per recruiting country.
const COORDS = {
  Senegal: [-14.5, 14.6], Nigeria: [8.7, 9.1], Benin: [2.3, 9.5], Togo: [1.0, 8.2], Kenya: [37.9, 0.4],
  "Sierra Leone": [-11.8, 8.5], Liberia: [-9.4, 6.5], "Ivory Coast": [-5.5, 7.6],
  Guinea: [-11.2, 10.6], "Burkina Faso": [-1.7, 12.6], Mali: [-3.5, 17.6], Niger: [8.1, 17.6],
};

export const AfricaDistributorMap = ({
  countries = DISTRIBUTOR_COUNTRIES,
  projectionConfig = { scale: 470, center: [17, 2] },
  hintText,
  testId = "distributor-map",
}) => {
  const { t, lang } = useLang();
  const geoToCountry = useMemo(() => Object.fromEntries(countries.map((c) => [geoName(c), c])), [countries]);
  const highlight = useMemo(() => new Set(Object.keys(geoToCountry)), [geoToCountry]);
  const [counts, setCounts] = useState({});
  const [appointed, setAppointed] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [pin, setPin] = useState(null);

  const fetchStats = useCallback(() => {
    api.get("/distributors/stats")
      .then(({ data }) => { setCounts(data.counts || {}); setLoaded(true); })
      .catch(() => setLoaded(false));
    api.get("/distributors/appointed")
      .then(({ data }) => setAppointed(Array.isArray(data) ? data : []))
      .catch(() => setAppointed([]));
  }, []);

  useEffect(() => {
    fetchStats();
    const onApplied = () => fetchStats();
    window.addEventListener("distributor-applied", onApplied);
    return () => window.removeEventListener("distributor-applied", onApplied);
  }, [fetchStats]);

  const total = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);
  const label = (n) => `${n} ${n === 1 ? t.distributor.application : t.distributor.applications}`;

  // Appointed distributor pins that belong to this map instance, scattered around each country centroid.
  const pins = useMemo(() => {
    return appointed
      .filter((d) => highlight.has(geoName(d.country)) && COORDS[d.country])
      .map((d, i) => {
        const [bx, by] = COORDS[d.country];
        const [dx, dy] = offsetFor(`${d.country}|${d.city}|${d.company}|${i}`);
        return { ...d, coords: [bx + dx, by + dy], key: `${d.country}-${d.city}-${d.company}-${i}` };
      });
  }, [appointed, highlight]);

  const applyHere = (country) => {
    window.dispatchEvent(new CustomEvent("select-distributor-country", { detail: country }));
    setSelected(null);
  };

  return (
    <div className="relative rounded-2xl border border-white/10 bg-black/40 p-4 md:p-6" data-testid={testId}>
      <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-white/35 flex items-center gap-1.5">
        <MapPin size={12} /> {hintText || t.distributor.mapHint}
      </p>
      <div className="relative">
        <ComposableMap projection="geoMercator" projectionConfig={projectionConfig} width={800} height={680} style={{ width: "100%", height: "auto" }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const gname = geo.properties.name;
                const country = geoToCountry[gname];
                const on = highlight.has(gname);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={on ? () => setSelected(country) : undefined}
                    onMouseEnter={on ? () => setHovered(country) : undefined}
                    onMouseLeave={on ? () => setHovered(null) : undefined}
                    style={{
                      default: { fill: on ? "#00A86B" : "#1e221f", stroke: on ? "#7CFC00" : "#2a2f2b", strokeWidth: on ? 0.9 : 0.4, outline: "none", cursor: on ? "pointer" : "default" },
                      hover: { fill: on ? "#12b877" : "#262b27", stroke: "#3a403a", strokeWidth: 0.5, outline: "none", cursor: on ? "pointer" : "default" },
                      pressed: { fill: on ? "#00A86B" : "#1e221f", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {countries.map((c) => {
            const coords = COORDS[c];
            if (!coords) return null;
            const n = counts[c] ?? 0;
            const show = hovered === c || selected === c;
            return (
              <Marker key={c} coordinates={coords} onClick={() => setSelected(c)} onMouseEnter={() => setHovered(c)} onMouseLeave={() => setHovered(null)} style={{ default: { cursor: "pointer" } }} data-testid={`map-marker-${c}`}>
                <circle r={9} fill="#7CFC00" opacity={0.28}>
                  <animate attributeName="r" values="9;15;9" dur="2.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.32;0;0.32" dur="2.6s" repeatCount="indefinite" />
                </circle>
                <circle r={show ? 6 : 4.5} fill={n > 0 ? "#7CFC00" : "#c9ffa1"} stroke="#0A0A0A" strokeWidth={1.2} style={{ cursor: "pointer" }} />
                {show && (
                  <text textAnchor="middle" y={-13} style={{ fontFamily: "system-ui", fontWeight: 700, fill: "#FAFAF7", fontSize: 14, paintOrder: "stroke", stroke: "#0A0A0A", strokeWidth: 3.5, strokeLinejoin: "round", pointerEvents: "none" }}>
                    {c}{loaded && n > 0 ? ` · ${n}` : ""}
                  </text>
                )}
              </Marker>
            );
          })}

          {/* Appointed distributor pins — real partners at their city, inside their country */}
          {pins.map((p) => {
            const active = pin === p.key;
            return (
              <Marker
                key={p.key}
                coordinates={p.coords}
                onMouseEnter={() => setPin(p.key)}
                onMouseLeave={() => setPin(null)}
                data-testid="appointed-pin"
                style={{ default: { cursor: "pointer" } }}
              >
                <circle r={active ? 4.5 : 3.2} fill="#FAFAF7" stroke="#0A0A0A" strokeWidth={1} />
                {active && (
                  <text textAnchor="middle" y={-9} style={{ fontFamily: "system-ui", fontWeight: 700, fill: "#FAFAF7", fontSize: 12, paintOrder: "stroke", stroke: "#0A0A0A", strokeWidth: 3, strokeLinejoin: "round", pointerEvents: "none" }}>
                    {[p.city, p.company].filter(Boolean).join(" · ") || p.country}
                  </text>
                )}
              </Marker>
            );
          })}
        </ComposableMap>

        {/* Country opportunity blurb */}
        <AnimatePresence>
          {selected && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}
              className="absolute left-3 right-3 bottom-3 md:left-4 md:right-auto md:bottom-4 md:w-80 max-h-[70%] overflow-y-auto rounded-xl border border-lime2/30 bg-[#0e120f]/95 backdrop-blur-sm p-5 shadow-2xl"
              data-testid="map-story-card"
            >
              <button onClick={() => setSelected(null)} data-testid="map-story-close" className="absolute top-3 right-3 text-white/40 hover:text-white"><X size={16} /></button>
              <p className="text-[11px] uppercase tracking-[0.18em] text-lime2">{t.distributor.opportunity}</p>
              <h4 className="mt-1 font-display text-2xl font-bold text-white">{selected}</h4>
              {loaded && (counts[selected] ?? 0) > 0 && <p className="text-xs text-lime2/80 mt-0.5">{label(counts[selected])}</p>}
              <p className="mt-3 text-sm text-white/70 leading-relaxed">{(COUNTRY_BLURBS[selected] || {})[lang === "fr" ? "fr" : "en"]}</p>
              <button onClick={() => applyHere(selected)} data-testid="map-story-cta" className="mt-4 inline-flex items-center gap-2 rounded-full bg-lime2 px-4 py-2 text-sm font-semibold text-black hover:brightness-110 transition">
                {t.distributor.mapCTA} <ArrowRight size={14} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Legend */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3 px-1">
        <span className="inline-flex items-center gap-4">
          <span className="inline-flex items-center gap-2 text-xs text-white/50">
            <span className="inline-block h-3 w-3 rounded-sm bg-lime2" /> {t.distributor.countriesLabel} · {countries.length}
          </span>
          {pins.length > 0 && (
            <span className="inline-flex items-center gap-2 text-xs text-white/50" data-testid="appointed-legend">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-white border border-black" /> {t.distributor.appointedLabel} · {pins.length}
            </span>
          )}
        </span>
        {loaded && <span className="text-xs text-white/50" data-testid="map-total">{label(total)} {t.distributor.totalSuffix}</span>}
      </div>
    </div>
  );
};
